import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Plus, Trash2, Archive, Search, Settings, HelpCircle, LogOut, Globe, RotateCcw, FileCheck, Pen, LayoutTemplate, FileText, BookOpen, Compass, Puzzle } from 'lucide-react';
import { Session } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import { ToolType, ToolHistoryListItem, User } from '../services/api';

interface ArchivedSession {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

interface SidebarProps {
  sessions: Session[];
  currentSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
  onNewSession: () => void;
  onSessionDelete: (sessionId: string) => void;
  onSessionArchive: (sessionId: string) => void;
  onLanguageSelect: () => void;
  currentLanguage: string;
  // 工具按钮回调
  onLibraryOpen?: () => void;
  onLegalDrafterOpen?: () => void;

  // 归档相关
  archivedSessions?: ArchivedSession[];
  onSessionRestore?: (sessionId: string) => void;
  // 工具历史相关
  toolHistoryRecords?: ToolHistoryListItem[];
  onToolHistorySelect?: (record: ToolHistoryListItem) => void;
  onToolHistoryDelete?: (recordId: string) => void;
  // 登出回调
  onLogout?: () => void;
  // 定价页面回调
  onPricingOpen?: () => void;
  // 用户信息
  user?: User | null;
  // 设置页面回调
  onSettingsOpen?: () => void;
  onGuideOpen?: () => void;
  // 🧩 Architecture 3.0: Creator Studio
  onStudioOpen?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  sessions,
  currentSessionId,
  onSessionSelect,
  onNewSession,
  onSessionDelete,
  onSessionArchive,
  onLanguageSelect,
  currentLanguage,
  onLibraryOpen,
  onLegalDrafterOpen,

  archivedSessions = [],
  onSessionRestore,
  toolHistoryRecords = [],
  onToolHistorySelect,
  onToolHistoryDelete,
  onLogout,
  onPricingOpen,
  onSettingsOpen,
  onGuideOpen,
  onStudioOpen,
  user,
}) => {
  const { t } = useTranslation(currentLanguage);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [activeTab, setActiveTab] = useState<'chats' | 'archived'>('chats');
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Debug: 检查用户对象
  useEffect(() => {
    console.log('[Sidebar] User object received:', user);
  }, [user]);

  // 过滤会话列表
  const filteredSessions = sessions.filter(session =>
    session.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 过滤归档会话列表
  const filteredArchivedSessions = archivedSessions.filter(session =>
    session.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 过滤工具历史列表
  const filteredToolHistory = toolHistoryRecords.filter(record =>
    record.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 合并后的列表（显示所有项目）
  type CombinedItem =
    | { type: 'chat'; data: typeof sessions[0]; timestamp: number }
    | { type: ToolType; data: ToolHistoryListItem; timestamp: number };

  const combinedItems: CombinedItem[] = (() => {
    let items: CombinedItem[] = [];

    // 添加聊天会话
    items = items.concat(
      filteredSessions.map(session => ({
        type: 'chat' as const,
        data: session,
        timestamp: session.updated_at,
      }))
    );

    // 添加工具历史
    const toolItems = filteredToolHistory
      .map(record => ({
        type: record.toolType,
        data: record,
        timestamp: new Date(record.updatedAt).getTime(),
      }));
    items = items.concat(toolItems);

    // 按时间倒序排列
    return items.sort((a, b) => b.timestamp - a.timestamp);
  })();

  // 获取工具类型对应的图标
  const getToolIcon = (toolType: ToolType | 'chat') => {
    switch (toolType) {
      case 'chat': return MessageCircle;
      case 'review': return FileCheck;
      case 'draft': return Pen;
      case 'template': return LayoutTemplate;
      case 'form': return FileText;
      default: return MessageCircle;
    }
  };

  // 点击外部关闭用户菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // 获取用户显示名称
  const getUserDisplayName = () => {
    if (user?.email && user.email.trim()) return user.email;
    if (user?.name && user.name.trim() && user.name !== 'User') return user.name;
    return t('user');
  };

  // 获取用户头像首字母
  const getUserInitial = () => {
    if (user?.email && user.email.trim()) return user.email.charAt(0).toUpperCase();
    if (user?.name && user.name.trim()) return user.name.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <div className="w-64 bg-chat-bg border-r border-gray-200 flex flex-col h-screen">
      {/* 新建对话按钮 */}
      <div className="p-4 border-b border-gray-200">
        <button
          onClick={onNewSession}
          className="w-full flex items-center gap-2 px-4 py-2 bg-chat-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Plus size={16} />
          <span className="text-sm font-medium">{t('newChat')}</span>
        </button>
      </div>

      {/* AI 工具按钮区域 - 与侧边栏一致的简约风格 */}
      <div className="px-3 py-2 border-b border-gray-200 flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onLibraryOpen}
            className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            title={t('library')}
          >
            <BookOpen size={14} className="text-gray-500" />
            <span className="text-xs font-medium text-gray-700">{t('library')}</span>
          </button>

          <button
            onClick={onLegalDrafterOpen}
            className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            title={t('aiDraft')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
              <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
              <path d="M2 2l7.586 7.586"></path>
            </svg>
            <span className="text-xs font-medium text-gray-700">{t('aiDraft')}</span>
          </button>
        </div>

        {/* 🧩 Architecture 3.0: Studio (计划设计) 入口按钮 */}
        <button
          onClick={onStudioOpen}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-200 transition-colors"
          title="计划设计"
        >
          <Puzzle size={14} className="text-purple-500" />
          <span className="text-xs font-medium text-gray-700 text-purple-700">计划设计</span>
        </button>
      </div>

      {/* 归档视图标题栏 - 仅在归档模式下显示 */}
      {activeTab === 'archived' && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Archive size={18} className="text-gray-600" />
              <span className="text-sm font-semibold text-gray-800">{t('archived')}</span>
              {archivedSessions.length > 0 && (
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                  {archivedSessions.length}
                </span>
              )}
            </div>
            <button
              onClick={() => setActiveTab('chats')}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              ← {t('backToChats') || 'Back'}
            </button>
          </div>
        </div>
      )}

      {/* 搜索框 */}
      <div className="px-4 py-2 border-b border-gray-200">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={16} className="text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('search')}
            className="w-full pl-10 pr-3 py-1 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:outline-none transition-colors"
          />
        </div>
      </div>



      {/* 历史对话列表 / 归档列表 */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-2">
          <div className="space-y-1">
            {activeTab === 'chats' ? (
              // 合并的活跃历史列表（对话 + 工具历史）
              combinedItems.length > 0 ? (
                combinedItems.map((item) => {
                  const IconComponent = getToolIcon(item.type);
                  const isChat = item.type === 'chat';
                  const itemId = isChat
                    ? (item.data as typeof sessions[0]).session_id
                    : (item.data as ToolHistoryListItem).id;
                  const itemTitle = isChat
                    ? (item.data as typeof sessions[0]).title
                    : (item.data as ToolHistoryListItem).title;

                  return (
                    <div
                      key={`${item.type}-${itemId}`}
                      className={`group relative flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isChat && currentSessionId === itemId
                        ? 'bg-chat-warm text-gray-900'
                        : 'hover:bg-chat-white text-gray-700'
                        }`}
                    >
                      <button
                        onClick={() => {
                          if (isChat) {
                            onSessionSelect(itemId);
                          } else {
                            onToolHistorySelect?.(item.data as ToolHistoryListItem);
                          }
                        }}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      >
                        <IconComponent size={14} className={`flex-shrink-0 ${item.type === 'chat' ? 'text-gray-400' :
                          item.type === 'review' ? 'text-green-500' :
                            item.type === 'draft' ? 'text-blue-500' :
                              item.type === 'template' ? 'text-purple-500' :
                                'text-orange-500'
                          }`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {itemTitle}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(item.timestamp).toLocaleDateString('en-US')}
                          </div>
                        </div>
                      </button>

                      {/* 操作按钮 */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {isChat && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSessionArchive(itemId);
                            }}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                            title="Archive Chat"
                          >
                            <Archive size={12} className="text-gray-500 hover:text-blue-600" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isChat) {
                              onSessionDelete(itemId);
                            } else {
                              onToolHistoryDelete?.(itemId);
                            }
                          }}
                          className="p-1 hover:bg-red-100 rounded transition-colors"
                          title={isChat ? "Delete Chat" : "Delete"}
                        >
                          <Trash2 size={12} className="text-gray-500 hover:text-red-600" />
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-400 text-sm">
                  <MessageCircle size={24} className="mx-auto mb-2 opacity-50" />
                  {t('noArchivedChats')}
                </div>
              )
            ) : (
              // 归档对话列表
              filteredArchivedSessions.length > 0 ? (
                filteredArchivedSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`group relative flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${currentSessionId === session.id
                      ? 'bg-chat-warm text-gray-900'
                      : 'hover:bg-chat-white text-gray-700'
                      }`}
                  >
                    <button
                      onClick={() => onSessionSelect(session.id)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                      <Archive size={14} className="text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {session.title}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(session.updatedAt).toLocaleDateString('en-US')}
                        </div>
                      </div>
                    </button>

                    {/* 恢复按钮 */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSessionRestore?.(session.id);
                        }}
                        className="p-1 hover:bg-green-100 rounded transition-colors"
                        title={t('restore')}
                      >
                        <RotateCcw size={12} className="text-gray-500 hover:text-green-600" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSessionDelete(session.id);
                        }}
                        className="p-1 hover:bg-red-100 rounded transition-colors"
                        title="Delete Chat"
                      >
                        <Trash2 size={12} className="text-gray-500 hover:text-red-600" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-400 text-sm">
                  <Archive size={24} className="mx-auto mb-2 opacity-50" />
                  {t('noArchivedChats')}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* 底部用户信息 */}
      <div className="relative" ref={userMenuRef}>
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="w-full flex items-center justify-between text-sm text-gray-600 hover:bg-gray-50 rounded-lg p-2 transition-colors"
          >
            <div className="flex items-center gap-2">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name || 'User'}
                  className="w-6 h-6 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-xs font-semibold text-gray-600">
                    {getUserInitial()}
                  </span>
                </div>
              )}
              <span>{getUserDisplayName()}</span>
            </div>
          </button>
        </div>

        {/* 用户菜单 */}
        {showUserMenu && (
          <div className="absolute bottom-full left-4 right-4 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-10">
            {/* 用户信息 */}
            <div className="px-4 py-3 border-b border-gray-100">
              <div>
                <div className="text-sm font-medium text-gray-900">{getUserDisplayName()}</div>
                <div className="text-xs text-gray-500">
                  {t('personal')} · {
                    user?.tier ? (
                      user.tier === 'enterprise' ? 'Enterprise' :
                        user.tier === 'ultra' ? 'Ultra Plan' :
                          user.tier === 'max' ? 'Max Plan' :
                            user.tier === 'pro' ? 'Pro Plan' :
                              t('freePlan')
                    ) : t('freePlan')
                  }
                </div>
              </div>
            </div>

            {/* 菜单项 */}
            <div className="py-1">
              <button
                onClick={() => {
                  setActiveTab('archived');
                  setShowUserMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Archive size={16} className="text-gray-500" />
                <span>{t('archived')}</span>
                {archivedSessions.length > 0 && (
                  <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                    {archivedSessions.length}
                  </span>
                )}
              </button>

              <button
                onClick={() => {
                  setShowUserMenu(false);
                  onSettingsOpen?.();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Settings size={16} className="text-gray-500" />
                <span>{t('settings')}</span>
              </button>

              <button
                onClick={onLanguageSelect}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Globe size={16} className="text-gray-500" />
                <span>{t('language')}</span>
              </button>

              <a
                href="mailto:support@lawhelper.ai"
                onClick={() => setShowUserMenu(false)}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <HelpCircle size={16} className="text-gray-500 mt-1 self-start" />
                <div className="flex flex-col items-start leading-tight">
                  <span className="font-medium">{t('getHelp')}</span>
                  <span className="text-xs text-gray-500">support@lawhelper.ai</span>
                </div>
              </a>

              <button
                onClick={() => {
                  setShowUserMenu(false);
                  onGuideOpen?.();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Compass size={16} className="text-gray-500" />
                <span>{t('onboardingGuide')}</span>
              </button>

              <div className="border-t border-gray-100 my-1"></div>

              <button
                onClick={() => {
                  setShowUserMenu(false);
                  onPricingOpen?.();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <span>{t('viewAllPlans')}</span>
              </button>



              <div className="border-t border-gray-100 my-1"></div>

              <button
                onClick={() => {
                  setShowUserMenu(false);
                  onLogout?.();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <LogOut size={16} className="text-gray-500" />
                <span>{t('logOut')}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;